import { useCallback, useEffect, useRef, useState } from "react";
import { getApi, postApi } from "../../../services/api/globalApi";

const CALCULATE_PATH = "admin/reports/monthly-sales/calculate";
const STATUS_PATH = `${CALCULATE_PATH}/status`;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_FAILURES = 5;

const PHASE_LABELS = {
  "loading-users": "در حال بارگذاری کاربران...",
  "calculating-previous-month": "مرحله ۱ از ۲: محاسبه فروش ماه قبل",
  "rechecking-recent-months": "مرحله ۲ از ۲: بررسی مجدد ماه‌های اخیر",
};

export default function useMonthlySalesCalculation({ onFinished } = {}) {
  const [job, setJob] = useState(null);
  const [message, setMessage] = useState(null);
  const [pollFailures, setPollFailures] = useState(0);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const isRunning = job?.status === "running";

  useEffect(() => {
    let ignore = false;
    getApi({ path: STATUS_PATH })
      .then((response) => {
        const current = response?.data?.result;
        if (!ignore && current?.status === "running") setJob(current);
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!isRunning) return undefined;
    let ignore = false;
    const timer = setTimeout(async () => {
      try {
        const response = await getApi({ path: STATUS_PATH });
        if (ignore) return;
        setPollFailures(0);
        setJob(response?.data?.result);
      } catch (error) {
        if (ignore) return;
        setPollFailures((count) => count + 1);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [job, isRunning, pollFailures]);

  useEffect(() => {
    if (pollFailures < MAX_POLL_FAILURES) return;
    setPollFailures(0);
    setJob(null);
    setMessage({
      type: "error",
      text: "ارتباط با سرور برای دریافت وضعیت محاسبه قطع شد. محاسبه ممکن است هنوز در حال اجرا باشد؛ صفحه را دوباره باز کنید.",
    });
  }, [pollFailures]);

  useEffect(() => {
    if (job?.status === "completed") {
      const processed = job.result?.usersProcessed;
      setMessage({
        type: "success",
        text: `محاسبه فروش ماه قبل با موفقیت انجام شد.${
          processed ? ` (${processed.toLocaleString("fa-IR")} کاربر)` : ""
        }`,
      });
      onFinishedRef.current?.();
    } else if (job?.status === "failed") {
      setMessage({
        type: "error",
        text: `محاسبه فروش ماهانه با خطا مواجه شد${
          job.error ? `: ${job.error}` : "."
        }`,
      });
    }
  }, [job]);

  const start = useCallback(async () => {
    setMessage(null);
    try {
      const response = await postApi({ path: CALCULATE_PATH, body: {} });
      const { started, job: startedJob } = response?.data?.result || {};
      setPollFailures(0);
      setJob(startedJob);
      if (started === false) {
        setMessage({
          type: "info",
          text: "یک محاسبه از قبل در حال اجراست؛ پیشرفت آن نمایش داده می‌شود.",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "خطا در شروع محاسبه فروش ماهانه." });
    }
  }, []);

  const total = job?.total || 0;
  const percent =
    isRunning && job?.phase !== "loading-users" && total > 0
      ? Math.min(100, Math.round((job.processed / total) * 100))
      : null;

  return {
    start,
    isRunning,
    message,
    dismissMessage: () => setMessage(null),
    percent,
    phaseLabel: isRunning ? PHASE_LABELS[job?.phase] || "در حال شروع..." : null,
  };
}
